import { useCallback, useId, useLayoutEffect, useRef, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { OverlayScrollArea } from '@odo/shared-ui';
import { useRejectedScans } from '../../../hooks/useRejectedScans';
import { ModalShell } from '../../shared/ModalShell';
import { ErrorBanner } from '../actions/dm/DmBanners';
import {
    focusFirstIn,
    focusWhenAble,
    rememberTrigger,
    type TriggerMemory
} from '../actions/dm/control/layerFocus';
import { RejectedScanCard } from './RejectedScanCard';
import { RejectedScanDetail } from './RejectedScanDetail';
import { RejectedScanPhotoViewer, type OpenedPhotos } from './RejectedScanPhotoViewer';
import { truncatedListNote } from './rejectedScanText';
import { LIST_NAV, adjacentScanId, stepBack, type RejectedScansNav } from './rejectedScansNav';

const LIST_SKELETON_ROWS = 4;
const OVERLAY_LAYER_CLASS = 'absolute inset-0 z-10 bg-white';

type Level = 0 | 1 | 2;

interface RejectedScansListProps {
    query: ReturnType<typeof useRejectedScans>;
    onOpenScan: (scanId: number) => void;
}

function RejectedScansList({ query, onOpenScan }: RejectedScansListProps) {
    const { data, error, isFetching, refetch } = query;

    if (error && !isFetching) {
        return <ErrorBanner message={error.message} onRetry={() => void refetch()} />;
    }
    if (!data) {
        return (
            <div className="flex flex-col gap-3" role="status" aria-label="Loading rejected scans">
                {Array.from({ length: LIST_SKELETON_ROWS }, (_, index) => (
                    <div key={index} className="h-20 rounded-xl bg-gray-200 animate-pulse" />
                ))}
            </div>
        );
    }
    if (data.scans.length === 0) {
        return (
            <p className="py-12 text-center text-sm text-gray-500">
                No rejected scans for this patient
            </p>
        );
    }

    const note = truncatedListNote(data.scans.length, data.total);
    return (
        <div className="flex flex-col gap-3">
            {data.scans.map((scan) => (
                <RejectedScanCard key={scan.scanId} scan={scan} onOpen={onOpenScan} />
            ))}
            {note && <p className="text-center text-xs text-gray-500">{note}</p>}
        </div>
    );
}

// Covered layers stay mounted (keeping their scroll) but inert, since React 18 has no `inert` prop.
function useLayerFocus(depth: Level) {
    const listLayer = useRef<HTMLDivElement>(null);
    const scanLayer = useRef<HTMLDivElement>(null);
    const photoLayer = useRef<HTMLDivElement>(null);
    const triggers = useRef<Partial<Record<Level, TriggerMemory | null>>>({});
    const shownDepth = useRef(depth);

    useLayoutEffect(() => {
        const layers = [listLayer.current, scanLayer.current, photoLayer.current];
        layers.forEach((layer, level) => {
            if (layer) layer.inert = level < depth;
        });
        const from = shownDepth.current;
        shownDepth.current = depth;
        const top = layers[depth];
        if (from === depth || !top) return;
        if (depth > from) focusFirstIn(top);
        else focusWhenAble(() => triggers.current[depth] ?? null, top);
    }, [depth]);

    const rememberClickIn = (level: Level) => (event: MouseEvent) => {
        const layer = level === 0 ? listLayer.current : scanLayer.current;
        triggers.current = { ...triggers.current, [level]: rememberTrigger(event.target, layer) };
    };

    return { listLayer, scanLayer, photoLayer, rememberClickIn };
}

function useRejectedScansNav(onClose: () => void) {
    const [nav, setNav] = useState<RejectedScansNav>(LIST_NAV);
    const scanScroll = useRef<HTMLDivElement | null>(null);

    const dismiss = useCallback(() => {
        const previous = stepBack(nav);
        if (previous) setNav(previous);
        else onClose();
    }, [nav, onClose]);
    const openScan = useCallback((scanId: number) => {
        setNav({ scanId, photos: null });
        scanScroll.current?.scrollTo({ top: 0 });
    }, []);
    const openPhotos = useCallback(
        (photos: OpenedPhotos) => setNav((current) => ({ ...current, photos })),
        []
    );
    const backToList = useCallback(() => setNav(LIST_NAV), []);

    return { nav, scanScroll, dismiss, openScan, openPhotos, backToList };
}

interface RejectedScansLayersProps {
    query: ReturnType<typeof useRejectedScans>;
    navigation: ReturnType<typeof useRejectedScansNav>;
}

function RejectedScansLayers({ query, navigation }: RejectedScansLayersProps) {
    const { nav, scanScroll, dismiss, openScan, openPhotos, backToList } = navigation;
    const scans = query.data?.scans ?? [];
    const scan = scans.find((item) => item.scanId === nav.scanId);
    const depth: Level = !scan ? 0 : nav.photos ? 2 : 1;
    const { listLayer, scanLayer, photoLayer, rememberClickIn } = useLayerFocus(depth);

    return (
        <div className="relative flex-1 min-h-0">
            <div ref={listLayer} className="h-full" onClickCapture={rememberClickIn(0)}>
                <OverlayScrollArea className="h-full" contentClassName="px-6 py-4">
                    <RejectedScansList query={query} onOpenScan={openScan} />
                </OverlayScrollArea>
            </div>
            {scan && (
                <div
                    ref={scanLayer}
                    className={OVERLAY_LAYER_CLASS}
                    onClickCapture={rememberClickIn(1)}
                >
                    <OverlayScrollArea
                        className="h-full"
                        contentClassName="px-6 py-4"
                        scrollRef={scanScroll}
                    >
                        <RejectedScanDetail
                            scan={scan}
                            previousScanId={adjacentScanId(scans, scan.scanId, -1)}
                            nextScanId={adjacentScanId(scans, scan.scanId, 1)}
                            onBack={backToList}
                            onOpenScan={openScan}
                            onOpenPhotos={openPhotos}
                        />
                    </OverlayScrollArea>
                </div>
            )}
            {scan && nav.photos && (
                <div ref={photoLayer} className={OVERLAY_LAYER_CLASS}>
                    <RejectedScanPhotoViewer opened={nav.photos} onBack={dismiss} />
                </div>
            )}
        </div>
    );
}

function RejectedScansHeader({ titleId, onClose }: { titleId: string; onClose: () => void }) {
    return (
        <header className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-200">
            <h2 id={titleId} className="text-xl font-bold text-gray-800">
                Rejected scans
            </h2>
            <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="-mr-1 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
            >
                <XMarkIcon className="h-6 w-6" />
            </button>
        </header>
    );
}

interface RejectedScansModalProps {
    patientId: string;
    onClose: () => void;
}

function RejectedScansModal({ patientId, onClose }: RejectedScansModalProps) {
    const titleId = useId();
    const query = useRejectedScans(patientId);
    const navigation = useRejectedScansNav(onClose);

    return createPortal(
        <ModalShell
            widthClassName="max-w-4xl"
            panelClassName="rounded-xl flex flex-col h-[85vh] overflow-hidden"
            dismissible
            onDismiss={navigation.dismiss}
            labelledBy={titleId}
        >
            <RejectedScansHeader titleId={titleId} onClose={onClose} />
            <RejectedScansLayers query={query} navigation={navigation} />
        </ModalShell>,
        document.body
    );
}

export { RejectedScansModal };
